import React, { useState, useEffect, useMemo } from 'react';
import { FaUndoAlt, FaTimes, FaMinus, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { submitReturnOnBehalf, getPostOrderSettings } from '../../services/ClientService';

const DEFAULT_REASONS = [
    'Returned at delivery (physical exchange)',
    'Wrong item received',
    'Item damaged during delivery',
    'Quality not as expected',
    'Other'
];

const ReturnRestoreModal = ({ isOpen, onClose, orderDetails, returnRequests = [], onSuccess, mode = 'restore' }) => {
    const isInstantRestore = mode === 'restore';
    const [returnReasons, setReturnReasons] = useState(DEFAULT_REASONS);
    const [selected, setSelected] = useState({});
    const [quantities, setQuantities] = useState({});
    const [reasons, setReasons] = useState({});
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        getPostOrderSettings()
            .then((res) => {
                const reasons = res?.data?.returnReasons;
                if (reasons?.length) setReturnReasons(reasons);
            })
            .catch(() => {});
        setSelected({});
        setQuantities({});
        setReasons({});
        setNotes('');
    }, [isOpen, orderDetails?._id]);

    const itemsWithPendingReturn = useMemo(() => {
        const set = new Set();
        (returnRequests || []).forEach((req) => {
            if (['Rejected', 'Closed'].includes(req.status)) return;
            (req.items || []).forEach((ri) => {
                if (ri.cartId) set.add(`cart:${ri.cartId}`);
                if (ri.productId) set.add(`pid:${ri.productId}`);
            });
        });
        return set;
    }, [returnRequests]);

    const eligibleItems = useMemo(() => {
        if (!orderDetails?.productsInfo) return [];
        return orderDetails.productsInfo
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => {
                if (item.isCancelled || item.isReturned) return false;
                if ((item.quantity || 1) <= 0) return false;
                const cartKey = item.cartId ? `cart:${item.cartId}` : null;
                const pid = item.productId?.toString() || item._id?.toString();
                if (cartKey && itemsWithPendingReturn.has(cartKey)) return false;
                if (pid && itemsWithPendingReturn.has(`pid:${pid}`)) return false;
                return true;
            });
    }, [orderDetails, itemsWithPendingReturn]);

    const selectedEntries = Object.entries(selected).filter(([, v]) => v);

    const estimatedRefund = selectedEntries.reduce((sum, [idx]) => {
        const { item } = eligibleItems.find((e) => String(e.index) === idx) || {};
        if (!item) return sum;
        const price = item.selectedWeight?.discountPrice || item.selectedWeight?.originalPrice || 0;
        const qty = quantities[idx] || item.quantity || 1;
        return sum + price * qty;
    }, 0);

    const toggleItem = (idx) => {
        setSelected((prev) => {
            const next = { ...prev, [idx]: !prev[idx] };
            if (next[idx]) {
                const item = eligibleItems.find((e) => String(e.index) === idx)?.item;
                setQuantities((q) => ({ ...q, [idx]: q[idx] || item?.quantity || 1 }));
                setReasons((r) => ({
                    ...r,
                    [idx]: r[idx] || returnReasons[0] || DEFAULT_REASONS[0]
                }));
            }
            return next;
        });
    };

    const updateQty = (idx, delta) => {
        const max = eligibleItems.find((e) => String(e.index) === idx)?.item?.quantity || 1;
        setQuantities((prev) => {
            const current = prev[idx] || 1;
            const next = Math.max(1, Math.min(max, current + delta));
            return { ...prev, [idx]: next };
        });
    };

    const handleSubmit = async () => {
        if (selectedEntries.length === 0) {
            toast.error('Select at least one product to return');
            return;
        }
        const missingReason = selectedEntries.find(([idx]) => !reasons[idx]);
        if (missingReason) {
            toast.error('Select a reason for each returned item');
            return;
        }

        const itemsPayload = selectedEntries.map(([idx]) => {
            const { item } = eligibleItems.find((e) => String(e.index) === idx) || {};
            return {
                cartId: item.cartId,
                productId: (item.productId || item._id)?.toString(),
                productName: item.productName,
                quantity: quantities[idx] || item.quantity || 1,
                reason: reasons[idx],
                description: notes.trim() || undefined
            };
        });

        setSubmitting(true);
        try {
            const response = await submitReturnOnBehalf({
                orderId: orderDetails._id,
                items: itemsPayload,
                reason: itemsPayload[0]?.reason,
                description: notes.trim() || undefined,
                completeAndRestore: isInstantRestore,
                skipReturnWindow: true
            });

            if (response?.success) {
                toast.success(
                    response.message ||
                        (isInstantRestore ? 'Return and Restore completed' : 'Return request submitted')
                );
                onSuccess?.(response.data);
                onClose();
            } else {
                toast.error(response?.message || 'Failed to process return');
            }
        } catch (err) {
            toast.error(err?.message || 'Failed to process return');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        <FaUndoAlt className="text-orange-600" />
                        <h3 className="text-lg font-bold text-gray-900">
                            {isInstantRestore ? 'Return and Restore' : 'Raise Return Request'}
                        </h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <FaTimes />
                    </button>
                </div>

                <div className="px-5 py-3 overflow-y-auto flex-1">
                    <p className="text-sm text-gray-600 mb-4">
                        {isInstantRestore
                            ? 'Use when the customer returns product(s) at delivery and receives replacement from the same order. Inventory is restored, billing is updated, and totals reflect the change immediately.'
                            : 'Submit a return request on behalf of the customer. It will appear in Return Management for approval, pickup scheduling, and refund processing.'}
                    </p>

                    {eligibleItems.length === 0 ? (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            No items are eligible for return on this order.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {eligibleItems.map(({ item, index }) => {
                                const idx = String(index);
                                const isSelected = !!selected[idx];
                                const maxQty = item.quantity || 1;
                                const price = item.selectedWeight?.discountPrice || item.selectedWeight?.originalPrice || 0;
                                const weightLabel = typeof item.selectedWeight?.weight === 'object'
                                    ? item.selectedWeight.weight.weightLabel || `${item.selectedWeight.weight.weightValue} ${item.selectedWeight.weight.metricLabel}`
                                    : item.selectedWeight?.weight;

                                return (
                                    <div
                                        key={item.cartId || item.productId || idx}
                                        className={`border rounded-lg p-3 ${isSelected ? 'border-orange-300 bg-orange-50/50' : 'border-gray-200'}`}
                                    >
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleItem(idx)}
                                                className="mt-1"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-gray-900 truncate">{item.productName}</p>
                                                {weightLabel && (
                                                    <p className="text-xs text-gray-500">{weightLabel}</p>
                                                )}
                                                <p className="text-sm text-gray-600">
                                                    ₹{price.toFixed(2)} × {maxQty} ordered
                                                </p>
                                            </div>
                                        </label>

                                        {isSelected && (
                                            <div className="mt-3 pl-7 space-y-3">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm text-gray-600">Return qty</span>
                                                    <div className="flex items-center border border-gray-300 rounded-md">
                                                        <button
                                                            type="button"
                                                            onClick={() => updateQty(idx, -1)}
                                                            className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                                                            disabled={(quantities[idx] || 1) <= 1}
                                                        >
                                                            <FaMinus className="w-3 h-3" />
                                                        </button>
                                                        <span className="px-3 text-sm font-medium">{quantities[idx] || 1}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => updateQty(idx, 1)}
                                                            className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                                                            disabled={(quantities[idx] || 1) >= maxQty}
                                                        >
                                                            <FaPlus className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-medium text-gray-600">Reason</label>
                                                    <select
                                                        value={reasons[idx] || ''}
                                                        onChange={(e) => setReasons((r) => ({ ...r, [idx]: e.target.value }))}
                                                        className="mt-1 w-full border border-gray-300 rounded-md text-sm px-2 py-1.5"
                                                    >
                                                        {returnReasons.map((r) => (
                                                            <option key={r} value={r}>{r}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-4">
                        <label className="text-xs font-medium text-gray-600">Notes (optional)</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            className="mt-1 w-full border border-gray-300 rounded-md text-sm px-2 py-1.5"
                            placeholder="e.g. Customer exchanged damaged unit at doorstep"
                        />
                    </div>

                    {selectedEntries.length > 0 && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
                            <div className="flex justify-between font-semibold text-gray-800">
                                <span>Estimated refund / adjustment</span>
                                <span>₹{estimatedRefund.toFixed(2)}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-5 py-4 border-t border-gray-200 flex gap-3 justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || eligibleItems.length === 0 || selectedEntries.length === 0}
                        className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg disabled:opacity-50"
                    >
                        {submitting
                            ? 'Processing…'
                            : isInstantRestore
                              ? 'Confirm Return and Restore'
                              : 'Submit Return Request'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReturnRestoreModal;
